// El ÚNICO lugar que escribe o borra el token de sesión del navegador.
//
// Nace de un bug real: el cierre por inactividad guarda una marca de actividad
// en el navegador (helpers/idle-session.js), y el login por clave la reseteaba
// pero el login con Google no. Resultado: el médico se autenticaba bien con
// Google y quedaba deslogueado en el mismo instante, porque heredaba la marca
// vieja del que había usado ese computador antes.
//
// La leccion no es "agregar la llamada en Google tambien": es que había TRES
// puertas que escribían `authToken` y nada obligaba a que la cuarta se
// acordara. Todas pasan por acá.
import { estaExento, limpiarMarca, marcarActividad, recordarExencion } from "./idle-session";
import { rekeyDrafts } from "./turno-draft-buffer";
import { rekeyActivityQueue } from "./activity-log";

/**
 * Deja una sesión iniciada en este navegador.
 *
 * Limpia primero: en un puesto compartido, el que entra no puede heredar ni la
 * ausencia ni la exención del anterior. Después marca actividad fresca, así el
 * reloj de inactividad arranca ahora y no en la visita pasada.
 *
 * @param {object} opts
 * @param {boolean} opts.mismoUsuario — true cuando el token se ROTA sin que
 *   cambie quién está sentado (cambiar la clave, "cerrar las otras sesiones",
 *   o que un admin revoque el token). Ahí borrar la exención dejaba a Hans
 *   deslogueándose al día siguiente: el guard solo la repone cuando vuelve
 *   /api/user/me/, y esos flujos no recargan la app. Un relevo de verdad pasa
 *   por el login, que sí la limpia.
 *   Mismo flag habilita re-keyear el write-ahead del Turno (`turno-draft-
 *   buffer.js`) del token viejo al nuevo: sin esto el médico quedaba
 *   deslogueado de su PROPIO borrador sin confirmar al rotar (#1255, 4ª
 *   pasada del revisor). `mismoUsuario: true` significa literalmente "es la
 *   misma persona", así que re-keyear no reabre el cruce entre médicos
 *   distintos (ese aislamiento pasa por el login, que no manda esta flag).
 *
 * @returns {boolean} false si el navegador no deja escribir (Safari privado,
 *   cuota llena). El llamador decide qué hacer: sin token no hay sesión.
 */
export const establecerSesion = (token, { mismoUsuario = false } = {}) => {
  if (!token) return false;
  const exencion = mismoUsuario ? estaExento() : false;
  let tokenAnterior = null;
  if (mismoUsuario) {
    try {
      tokenAnterior = localStorage.getItem("authToken");
    } catch {
      tokenAnterior = null;
    }
  }
  try {
    localStorage.setItem("authToken", token);
  } catch {
    return false;
  }
  if (tokenAnterior && tokenAnterior !== token) {
    try {
      rekeyDrafts(tokenAnterior, token);
    } catch {
      /* re-keyear nunca bloquea dejar la sesión rotada */
    }
    try {
      // Mismo motivo que los borradores: la telemetría sin enviar de ESTE
      // médico quedaría huérfana bajo la clave vieja si no se mueve. Mueve una
      // sola clave —la del dueño viejo— así que no reabre el cruce entre médicos.
      rekeyActivityQueue(tokenAnterior, token);
    } catch {
      /* ídem */
    }
  }
  limpiarMarca();
  marcarActividad({ forzar: true });
  if (exencion) recordarExencion(true);
  return true;
};

/** Borra la sesión de ESTE navegador. No revoca nada en el servidor. */
export const borrarSesionLocal = () => {
  try {
    localStorage.removeItem("authToken");
  } catch {
    /* Safari privado: cerrar sesión nunca se bloquea por el storage */
  }
  limpiarMarca();
};
